import React, { useState, Suspense } from "react";
import { ArrowLeft } from "lucide-react";
import InfoHint from "src/components/common/InfoHint";
import Dialog from "src/components/common/Dialog";
import { Button } from "src/components/shadcn/Button";
import { SendCardDollarsCheckoutLoading } from "./SendCardDollarsCheckout";
import AmountSelector from "./AmountSelector";
import { useOptimisticBalanceUpdate } from "./useOptimisticBalanceUpdate";
import { toast } from "src/components/shadcn/useToast";

const SendCardDollarsCheckout = React.lazy(
  () => import("./SendCardDollarsCheckout")
);

interface AddFundsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const MIN_DOLLARS = 15;
const MAX_DOLLARS = 5000;
const DEFAULT_AMOUNT = MIN_DOLLARS;

const AddFundsDialog: React.FC<AddFundsDialogProps> = ({
  isOpen,
  onClose,
  onComplete,
}) => {
  const [view, setView] = useState<"select-amount" | "payment">("select-amount");
  const [selectedAmount, setSelectedAmount] = useState<number>(DEFAULT_AMOUNT);
  const { updateBalanceOptimistically } = useOptimisticBalanceUpdate();

  const handleClose = () => {
    setView("select-amount");
    setSelectedAmount(DEFAULT_AMOUNT);
    onClose();
  };

  const handlePurchaseComplete = () => {
    // Optimistically update balance and poll for server confirmation
    updateBalanceOptimistically(selectedAmount, {
      onSuccess: () => {
        toast({
          title: "Funds added",
          description: `$${selectedAmount} has been added to your balance.`,
          variant: "success",
        });
      },
      onTimeout: () => {
        toast({
          title: "Payment processing",
          description: "Your payment is being processed. Your balance will update shortly.",
        });
      },
    });
    setView("select-amount");
    setSelectedAmount(DEFAULT_AMOUNT);
    onComplete();
  };

  const handleBack = () => {
    setView("select-amount");
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      title="Add Funds"
      className={view === "payment" ? "!max-w-[600px]" : "!max-w-[500px]"}
    >
      {view === "select-amount" ? (
        <div className="p-6">
          <div className="mb-6">
            <div className="mb-4">
              <AmountSelector
                value={selectedAmount}
                onChange={setSelectedAmount}
                min={MIN_DOLLARS}
                max={MAX_DOLLARS}
                step={1}
              />
            </div>
            <InfoHint>Select an amount to add to your balance (minimum ${MIN_DOLLARS}). Use the slider or type a custom amount.</InfoHint>
          </div>

          <div className="flex flex-col gap-3">
            <Button className="w-full" onClick={() => setView("payment")}>
              Continue to Payment
            </Button>
            <Button variant="secondary" className="w-full" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="p-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <Suspense fallback={<SendCardDollarsCheckoutLoading />}>
            <SendCardDollarsCheckout
              amount={selectedAmount}
              onComplete={handlePurchaseComplete}
            />
          </Suspense>
        </div>
      )}
    </Dialog>
  );
};

export default AddFundsDialog;
